import React, { useEffect, useState } from "react";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import { Check, X, Eye, ChevronDown, ChevronUp, RotateCcw, Plus, AlertCircle } from "lucide-react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { cn } from "../../lib/utils";
import { Agent } from "../../model_gen/agent_base";
import { Application } from "../../model_gen/application";
import { AgentService } from "../../services/AgentService";
import { ApplicationService } from "../../services/ApplicationService";
import { RoutingService } from "../../services/RoutingService";
import { Message } from "./AssistantContext";
import NewApplicationModal from "../Application/NewApplicationModal";
import { prepareRoutingRuleForSubmission } from "../../utils/routingRuleBuilder";
import { firstValueFrom } from "rxjs";

/**
 * Formats a routing rule condition into a human-readable description.
 * Aligns with the operator specifications in assistant_config_minimal.py.
 */
const formatRoutingCondition = (condition: any): string => {
  let conditionText = "";
  
  // Variable description
  if (condition.target_type === "request") {
    if (condition.variable_path === "last_message") {
      conditionText = "The last message";
    } else if (condition.variable_path === "last_user_message") {
      conditionText = "The user's last message";
    } else if (condition.variable_path === "last_assistant_message") {
      conditionText = "The assistant's last message";
    }
  } else if (condition.target_type === "state") {
    conditionText = `Form field "${condition.variable_path}"`;
  }
  
  // Operator and value description
  switch (condition.operator) {
    case "equals":
      if (condition.value_bool !== null && condition.value_bool !== undefined) {
        conditionText += ` is ${condition.value_bool ? "checked" : "unchecked"}`;
      } else {
        conditionText += ` equals "${condition.value_text || condition.value_number}"`;
      }
      break;
    case "not_equals":
      if (condition.value_bool !== null && condition.value_bool !== undefined) {
        conditionText += ` is ${condition.value_bool ? "unchecked" : "checked"}`;
      } else {
        conditionText += ` does not equal "${condition.value_text || condition.value_number}"`;
      }
      break;
    case "like":
      conditionText += ` contains "${condition.value_text}"`;
      break;
    case "matches":
    case "regex_match": // Keep for backward compatibility
      conditionText += ` matches pattern "${condition.value_text}"`;
      break;
    case "sem_match":
    case "semantic_match": // Keep for backward compatibility
      conditionText += ` semantically matches "${condition.value_text}"`;
      break;
    case "sem_similarity":
    case "semantic_similarity": // Keep for backward compatibility
      conditionText += ` is semantically similar to "${condition.value_text}"`;
      if (condition.comparison_op && condition.threshold_number !== null && condition.threshold_number !== undefined) {
        conditionText += ` (${condition.comparison_op} ${condition.threshold_number})`;
      }
      break;
    case "gt":
      conditionText += ` is greater than ${condition.value_number}`;
      break;
    case "gte":
      conditionText += ` is greater than or equal to ${condition.value_number}`;
      break;
    case "lt":
      conditionText += ` is less than ${condition.value_number}`;
      break;
    case "lte":
      conditionText += ` is less than or equal to ${condition.value_number}`;
      break;
    case "classify":
    case "classify_as": // Keep for backward compatibility
      conditionText += ` is classified as "${condition.value_text}"`;
      break;
    case "sentiment":
      const sentimentOp = condition.comparison_op === "IS_NOT" ? "does not have" : "has";
      conditionText += ` ${sentimentOp} ${condition.value_text} sentiment`;
      break;
    default:
      // Fallback to technical display
      conditionText = `${condition.target_type}.${condition.variable_path} ${condition.operator}`;
      if (condition.value_text) conditionText += ` "${condition.value_text}"`;
      if (condition.value_number !== null && condition.value_number !== undefined) conditionText += ` ${condition.value_number}`;
      if (condition.value_bool !== null && condition.value_bool !== undefined) conditionText += ` ${condition.value_bool}`;
  }
  
  return conditionText;
};

/**
 * Build a tree structure from flat conditions array for better visualization
 */
const buildConditionTree = (conditions: any[]): any => {
  if (!conditions || conditions.length === 0) return null;
  
  // Determine root operator - look at what logical_ops are used
  const rootOp = conditions.length > 1 && conditions[1].logical_op ? conditions[1].logical_op : "AND";
  
  const tree: any = {
    op: rootOp,
    isRoot: true,
    items: []
  };
  
  let currentGroup: any = null;
  let groupStack: any[] = [];
  
  conditions.forEach((cond, idx) => {
    const groupStart = cond.group_start || 0;
    const groupEnd = cond.group_end || 0;
    const logicalOp = idx === 0 ? undefined : (cond.logical_op || rootOp);
    
    // Open new groups
    for (let i = 0; i < groupStart; i++) {
      const newGroup: any = {
        op: logicalOp || rootOp,
        items: [],
        isGroup: true
      };
      
      if (currentGroup) {
        groupStack.push(currentGroup);
        currentGroup.items.push(newGroup);
      } else {
        tree.items.push(newGroup);
      }
      currentGroup = newGroup;
    }
    
    // Add condition
    const condItem = { ...cond, logicalOp, idx };
    if (currentGroup) {
      currentGroup.items.push(condItem);
      // Update group op based on this condition's logical_op
      if (logicalOp && currentGroup.items.length === 2) {
        currentGroup.op = logicalOp;
      }
    } else {
      tree.items.push(condItem);
    }
    
    // Close groups
    for (let i = 0; i < groupEnd; i++) {
      currentGroup = groupStack.pop() || null;
    }
  });
  
  // Update root op if needed
  if (tree.items.length > 1 && tree.items[1].logicalOp) {
    tree.op = tree.items[1].logicalOp;
  }
  
  return tree;
};

/**
 * Renders conditions in a nice tree view showing ALL/ANY grouping
 */
const ConditionTreeView: React.FC<{ conditions: any[] }> = ({ conditions }) => {
  const tree = buildConditionTree(conditions);
  
  if (!tree || !tree.items.length) {
    return <div className="text-xs text-muted-foreground">No conditions</div>;
  }
  
  const renderGroup = (group: any, depth: number = 0): JSX.Element => {
    const isRootLevel = depth === 0;
    const bgColor = depth === 0 ? "bg-muted/30" : depth === 1 ? "bg-muted/40" : "bg-muted/50";
    const borderColor = depth === 0 ? "border-muted" : depth === 1 ? "border-muted/70" : "border-muted/50";
    
    return (
      <div
        className={cn(
          "rounded-lg overflow-hidden",
          !isRootLevel && `border ${borderColor} ${bgColor} p-2 space-y-2`
        )}
      >
        {/* Group header showing ALL/ANY */}
        {group.items.length > 1 && (
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-muted-foreground">Match</span>
            <span className={cn(
              "text-xs font-semibold px-2 py-0.5 rounded",
              group.op === "AND" 
                ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                : "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300"
            )}>
              {group.op === "AND" ? "ALL" : "ANY"}
            </span>
            <span className="text-xs text-muted-foreground">of:</span>
          </div>
        )}
        
        {/* Items in this group */}
        <div className="space-y-2">
          {group.items.map((item: any, idx: number) => {
            if (item.isGroup) {
              // Nested group
              return (
                <div key={idx}>
                  {renderGroup(item, depth + 1)}
                </div>
              );
            } else {
              // Individual condition
              const conditionText = formatRoutingCondition(item);
              return (
                <div
                  key={item.idx || idx}
                  className="flex items-start gap-2 text-sm bg-background/80 rounded px-3 py-2 border border-border/50"
                >
                  <div className="flex-1 break-all">{conditionText}</div>
                </div>
              );
            }
          })}
        </div>
      </div>
    );
  };
  
  return renderGroup(tree);
};

interface InlineSuggestionsProps {
  message: Message;
  availableAgents: Agent[];
  availableApplications: Application[];
  accumulatedContext: Agent[];
  apiKey: ApiKeyOrGetter;
  onAddMessage: (content: string, mentions: Agent[], role?: "user" | "assistant", appliedChanges?: any[], isSystemMessage?: boolean, suggestions?: any) => void;
  onUpdateMessageSuggestions: (messageId: string, status: "approved" | "rejected" | "partial" | "ignored", appliedChanges?: any[]) => void;
  onToggleMessageExpanded: (messageId: string) => void;
  onSetPendingChanges: (changes: any) => void;
  onSetSelectedChanges: (changes: Set<number>) => void;
  onSetIsApproving: (isApproving: boolean) => void;
  onSetDiffViewData: (data: { title: string; original: string; proposed: string } | null) => void;
  onSetDiffViewOpen: (open: boolean) => void;
  onLoadAgents: (silent?: boolean) => void;
  onLoadApplications: (silent?: boolean) => void;
  onAddToAccumulatedContext: (agent: Agent) => void;
  pendingChanges: any;
  selectedChanges: Set<number>;
  isApproving: boolean;
  revertedMessageIds: Set<string>;
  onSetRevertedMessageIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  actionFormData: any;
  onSetActionFormData: React.Dispatch<React.SetStateAction<any>>;
  onSetPendingDecisionMarker: (marker: string | null) => void;
}

export const InlineSuggestions: React.FC<InlineSuggestionsProps> = ({
  message,
  availableAgents,
  availableApplications,
  accumulatedContext,
  apiKey,
  onAddMessage,
  onUpdateMessageSuggestions,
  onToggleMessageExpanded,
  onSetPendingChanges,
  onSetSelectedChanges,
  onSetIsApproving,
  onSetDiffViewData,
  onSetDiffViewOpen,
  onLoadAgents,
  onLoadApplications,
  onAddToAccumulatedContext,
  pendingChanges,
  selectedChanges,
  isApproving,
  revertedMessageIds,
  onSetRevertedMessageIds,
  actionFormData,
  onSetActionFormData,
  onSetPendingDecisionMarker
}) => {
  // Lazily load the applications list only when needed (e.g. CREATE_AGENT suggestions).
  useEffect(() => {
    if (
      message?.suggestions?.action === "CREATE_AGENT" &&
      availableApplications.length === 0
    ) {
      onLoadApplications(true);
    }
  }, [message?.suggestions?.action, availableApplications.length, onLoadApplications]);
  const [selectedApplicationId, setSelectedApplicationId] = useState<number | null>(null);
  const [isNewApplicationModalOpen, setIsNewApplicationModalOpen] = useState(false);

  // Helper function to strip redline tags
  const stripRedlineTags = (text: any) => {
    if (!text && text !== 0) return text;
    
    if (typeof text === "object" && text !== null) {
      return JSON.stringify(text, null, 2);
    }
    
    const textStr = typeof text === "string" ? text : String(text);
    
    if (typeof text === "string" && (text.trim().startsWith("{") || text.trim().startsWith("["))) {
      try {
        const parsed = JSON.parse(text);
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        // Not valid JSON, continue with normal processing
      }
    }
    
    return textStr.replace(/<REDLINE>|<\/REDLINE>/g, "");
  };

  // Helper function to generate a diff preview
  const generateDiffPreview = (original: string, proposed: string, maxLines: number = 3) => {
    const originalLines = original.split("\n");
    const proposedLines = proposed.split("\n");
    
    const changes: Array<{ type: "removed" | "added" | "unchanged", line: string }> = [];
    let removedCount = 0;
    let addedCount = 0;
    
    // Simple diff - just show first few changed lines
    let i = 0, j = 0;
    while ((removedCount + addedCount) < maxLines && (i < originalLines.length || j < proposedLines.length)) {
      if (i >= originalLines.length) {
        // Only proposed lines left
        changes.push({ type: "added", line: proposedLines[j] });
        addedCount++;
        j++;
      } else if (j >= proposedLines.length) {
        // Only original lines left
        changes.push({ type: "removed", line: originalLines[i] });
        removedCount++;
        i++;
      } else if (originalLines[i] === proposedLines[j]) {
        // Lines match
        i++;
        j++;
      } else {
        // Lines differ - show both
        if (removedCount < maxLines) {
          changes.push({ type: "removed", line: originalLines[i] });
          removedCount++;
        }
        if (addedCount < maxLines) {
          changes.push({ type: "added", line: proposedLines[j] });
          addedCount++;
        }
        i++;
        j++;
      }
    }
    
    const hasMore = i < originalLines.length || j < proposedLines.length || 
                    removedCount >= maxLines || addedCount >= maxLines;
    
    return { changes, hasMore };
  };

  // Local function for toggling selection
  const toggleChangeSelection = (index: number) => {
    const newSet = new Set(selectedChanges);
    if (newSet.has(index)) {
      newSet.delete(index);
    } else {
      newSet.add(index);
    }
    onSetSelectedChanges(newSet);
  };

  const handleRejectChanges = () => {
    onUpdateMessageSuggestions(message.id, "rejected");
    onSetSelectedChanges(new Set());
    onSetPendingChanges(null);
    
    // Set decision marker for rejection
    const changeCount = pendingChanges?.proposed_changes?.length || 0;
    onSetPendingDecisionMarker(
      `[USER DECISION] User rejected the previous suggestion (${changeCount} change${changeCount !== 1 ? "s" : ""} not applied).`
    );
    
    onAddMessage("I've decided not to apply these changes.", [], "assistant");
  };

  const handleApproveChanges = (explicitSelection?: Set<number>) => {
    if (!pendingChanges || !pendingChanges.proposed_changes) return;
    
    const selectionToUse = explicitSelection || selectedChanges;
    const changesToApprove = selectionToUse.size > 0
      ? pendingChanges.proposed_changes.filter((_: any, idx: number) => selectionToUse.has(idx))
      : pendingChanges.proposed_changes;
    
    if (changesToApprove.length === 0) {
      onAddMessage("I don't see any changes selected to approve. Please select at least one change.", [], "assistant");
      return;
    }
    
    onSetIsApproving(true);
    AgentService.approveAssistantChanges(
      changesToApprove,
      onSetIsApproving,
      apiKey
    ).subscribe({
      next: (result: any) => {
        const status = changesToApprove.length < pendingChanges.proposed_changes.length ? "partial" : "approved";
        onUpdateMessageSuggestions(message.id, status, changesToApprove);
        
        // Set decision marker for approvals
        if (status === "partial") {
          const totalChanges = pendingChanges.proposed_changes.length;
          const approvedCount = changesToApprove.length;
          const ignoredCount = totalChanges - approvedCount;
          onSetPendingDecisionMarker(
            `[USER DECISION] User partially approved ${approvedCount} of ${totalChanges} proposed changes. ${ignoredCount} change${ignoredCount !== 1 ? "s were" : " was"} not applied.`
          );
        } else {
          // Full approval
          const changeCount = changesToApprove.length;
          onSetPendingDecisionMarker(
            `[USER DECISION] User approved and applied all ${changeCount} proposed change${changeCount !== 1 ? "s" : ""}.`
          );
        }
        
        // Extract agent IDs from the approved changes and dispatch event
        const affectedAgentIds = changesToApprove
          .map((change: any) => change.agent_id)
          .filter((id: any) => id != null);
        
        if (affectedAgentIds.length > 0) {
          window.dispatchEvent(new CustomEvent("agentUpdated", {
            detail: { agentIds: affectedAgentIds }
          }));
        }
        
        if (pendingChanges.next_step) {
          onAddMessage(pendingChanges.next_step, [], "assistant");
        } else {
          onAddMessage("I've successfully applied the changes!", [], "assistant");
        }
        
        onLoadAgents(true);
        onSetPendingChanges(null);
        onSetIsApproving(false);
        onSetSelectedChanges(new Set());
      },
      error: (error) => {
        console.error("Error applying changes:", error);
        onAddMessage(`I'm sorry, I encountered an error applying the changes: ${error.message || "Unknown error"}. Could you try again?`, [], "assistant");
        onSetIsApproving(false);
      }
    });
  };

  const handleCreateAgent = () => {
    if (!pendingChanges || !pendingChanges.agent_data) return;
    
    const { agent_name, description, base_url, parent_application_id } = pendingChanges.agent_data;
    const applicationId = parent_application_id || pendingChanges.agent_data.application_id || selectedApplicationId;
    
    if (!applicationId) {
      onAddMessage("Please select an application for the new agent.", [], "assistant");
      return;
    }
    
    if (!agent_name || !description) {
      onAddMessage("I need both a name and description for the new agent. Could you provide those details?", [], "assistant");
      return;
    }
    
    onSetIsApproving(true);
    AgentService.createAgentFromAssistant(
      {
        ...pendingChanges.agent_data,
        application_id: applicationId
      },
      onSetIsApproving,
      apiKey
    ).subscribe({
      next: (result: any) => {
        onUpdateMessageSuggestions(message.id, "approved");
        
        // Add the newly created agent to accumulated context
        if (result && result.agent && result.agent.id) {
          const newAgent = {
            id: result.agent.id,
            agent_name: agent_name,
            description: description,
            ...result.agent
          };
          onAddToAccumulatedContext(newAgent);
        }
        
        // Dispatch agentUpdated event to refresh all agent-related components
        window.dispatchEvent(new CustomEvent("agentUpdated", {
          detail: { agentIds: result?.agent?.id ? [result.agent.id] : [] }
        }));
        
        if (pendingChanges.next_step) {
          onAddMessage(pendingChanges.next_step, [], "assistant");
        } else {
          onAddMessage(`Great! I've created the agent **${agent_name}**. What would you like to do next?`, [], "assistant");
        }
        
        onLoadAgents(true);
        onSetPendingChanges(null);
        onSetIsApproving(false);
        setSelectedApplicationId(null);
      },
      error: (error) => {
        console.error("Error creating agent:", error);
        onAddMessage(`I'm sorry, I ran into an issue creating the agent: ${error.message || "Unknown error"}. Could you try again?`, [], "assistant");
        onSetIsApproving(false);
      }
    });
  };

  const handleCreateAction = (overrideSelection?: Set<number>) => {
    if (!pendingChanges || !pendingChanges.action_data || !Array.isArray(pendingChanges.action_data)) return;
    
    const selection = overrideSelection || selectedChanges;
    
    if (selection.size === 0) {
      onAddMessage("Please select at least one capability to create.", [], "assistant");
      return;
    }
    
    const selectedActionData = pendingChanges.action_data
      .map((action: any, index: number) => ({ action, index }))
      .filter(({ index }: { index: number }) => selection.has(index))
      .map(({ action, index }: { action: any; index: number }) => ({
        ...action,
        ...(actionFormData[index] || {})
      }));
    
    for (const actionData of selectedActionData) {
      if (!actionData.agent_id || !actionData.action_name || !actionData.action_type || !actionData.description || !actionData.action_config) {
        onAddMessage("I'm missing some required information to create these capabilities. Let me gather the details I need.", [], "assistant");
        return;
      }
    }
    
    onSetIsApproving(true);
    AgentService.createActionFromAssistant(
      selectedActionData,
      onSetIsApproving,
      apiKey
    ).subscribe({
      next: (result: any) => {
        const createdActions = result.actions || [];
        const agentIds = Array.from(new Set(selectedActionData.map((a: any) => a.agent_id)));
        const agentNames = agentIds.map(id => {
          const agent = accumulatedContext.find(a => a.id === id) || 
                       availableAgents.find((a: any) => a.id === id && !a.isAction);
          return agent?.agent_name || `Agent ${id}`;
        }).join(", ");
        
        const status = selection.size < pendingChanges.action_data.length ? "partial" : "approved";
        onUpdateMessageSuggestions(message.id, status);
        
        // Dispatch agentUpdated event to refresh all agent-related components
        if (agentIds.length > 0) {
          window.dispatchEvent(new CustomEvent("agentUpdated", {
            detail: { agentIds }
          }));
        }
        
        if (pendingChanges.follow_up) {
          onAddMessage(pendingChanges.follow_up, [], "assistant");
        } else {
          const capabilityText = createdActions.length === 1 ? "capability" : "capabilities";
          onAddMessage(`Great! I've added the ${capabilityText} to ${agentNames}. What would you like to do next?`, [], "assistant");
        }
        
        onLoadAgents(true);
        onSetPendingChanges(null);
        onSetIsApproving(false);
        onSetActionFormData({});
      },
      error: (error) => {
        console.error("Error creating actions:", error);
        onAddMessage(`I'm sorry, I ran into an issue creating those capabilities: ${error.message || "Unknown error"}. Could you try again?`, [], "assistant");
        onSetIsApproving(false);
      }
    });
  };

  const handleAddSubagent = () => {
    if (!pendingChanges || !pendingChanges.subagent_data) return;
    
    const { parent_agent_id, child_agent_id } = pendingChanges.subagent_data;
    
    if (!parent_agent_id || !child_agent_id) {
      onAddMessage("I'm missing the parent or child agent information. Could you clarify which agents should be linked?", [], "assistant");
      return;
    }
    
    onSetIsApproving(true);
    AgentService.addSubagentFromAssistant(
      pendingChanges.subagent_data,
      onSetIsApproving,
      apiKey
    ).subscribe({
      next: (result: any) => {
        const parentAgent = availableAgents.find(a => a.id === parent_agent_id);
        const childAgent = availableAgents.find(a => a.id === child_agent_id);
        
        const parentName = parentAgent?.agent_name || `Agent ${parent_agent_id}`;
        const childName = childAgent?.agent_name || `Agent ${child_agent_id}`;
        
        onUpdateMessageSuggestions(message.id, "approved");
        
        onAddMessage(
          `I successfully linked **${childName}** as a subagent of **${parentName}**!\n\n${pendingChanges.next_step || "You can now add capabilities to the subagent."}`,
          [],
          "assistant"
        );
        
        onLoadAgents(true);
        onSetPendingChanges(null);
        onSetIsApproving(false);
      },
      error: (error) => {
        console.error("Error adding subagent:", error);
        onAddMessage(`I'm sorry, I ran into an issue linking those agents: ${error.message || "Unknown error"}. Could you try again?`, [], "assistant");
        onSetIsApproving(false);
      }
    });
  };

  const handleManageRouting = () => {
    if (!pendingChanges || pendingChanges.action !== "MANAGE_ROUTING") return;
    
    const operation = pendingChanges.operation;
    
    onSetIsApproving(true);
    
    if (operation === "create") {
      // Create routing rules using RoutingService
      const rules = pendingChanges.routing_rules || [pendingChanges.routing_rule];
      const agentId = pendingChanges.agent_id || rules[0]?.agent_id;
      
      if (!agentId) {
        onAddMessage("Error: No agent ID found for routing rule creation.", [], "assistant");
        onSetPendingChanges(null);
        onSetIsApproving(false);
        return;
      }
      
      // Process each rule
      const createdRules: any[] = [];
      let remainingRules = rules.length;
      let hasError = false;
      
      rules.forEach((assistantRule: any) => {
        // Build the complete rule using our helper
        const completeRule = prepareRoutingRuleForSubmission(assistantRule);
        
        RoutingService.createRule(agentId, completeRule, apiKey).subscribe({
          next: (savedRule: any) => {
            createdRules.push(savedRule);
            remainingRules--;
            
            if (remainingRules === 0 && !hasError) {
              onUpdateMessageSuggestions(message.id, "approved", createdRules);
              
              // Dispatch agentUpdated event to refresh all agent-related components
              window.dispatchEvent(new CustomEvent("agentUpdated", {
                detail: { agentIds: [agentId] }
              }));
              
              onAddMessage(
                pendingChanges.next_step || `Successfully created ${createdRules.length} routing rule(s).`,
                [],
                "assistant"
              );
              onSetPendingChanges(null);
              onSetIsApproving(false);
            }
          },
          error: (error: any) => {
            hasError = true;
            console.error("Error creating routing rule:", error);
            onAddMessage(
              `I encountered an error while creating a routing rule: ${error.message || "Unknown error"}`,
              [],
              "assistant"
            );
            onSetPendingChanges(null);
            onSetIsApproving(false);
          }
        });
      });
    } else if (operation === "edit") {
      // Edit routing rules using RoutingService
      const changes = pendingChanges.proposed_changes || [];
      
      if (changes.length === 0) {
        onAddMessage("Error: No changes found for routing rule edit.", [], "assistant");
        onSetPendingChanges(null);
        onSetIsApproving(false);
        return;
      }
      
      const editedRules: any[] = [];
      let remainingEdits = changes.length;
      let hasError = false;
      
      changes.forEach((change: any) => {
        const ruleId = change.rule_id;
        // Get agent_id from the change object (required from assistant)
        const agentId = change.agent_id || pendingChanges.agent_id;
        const proposed = change.proposed;
        
        if (!ruleId || !agentId) {
          hasError = true;
          onAddMessage(`Error: Missing rule ID (${ruleId}) or agent ID (${agentId}) for routing rule edit. The assistant must provide agent_id.`, [], "assistant");
          onSetPendingChanges(null);
          onSetIsApproving(false);
          return;
        }
        
        // Build the complete rule from proposed changes
        const completeRule = prepareRoutingRuleForSubmission(proposed);
        
        RoutingService.updateRule(agentId, ruleId, completeRule, apiKey).subscribe({
          next: (savedRule: any) => {
            editedRules.push(savedRule);
            remainingEdits--;
            
            if (remainingEdits === 0 && !hasError) {
              onUpdateMessageSuggestions(message.id, "approved", changes);
              
              // Extract agent IDs from changes and dispatch event
              const affectedAgentIds = changes
                .map((change: any) => change.agent_id)
                .filter((id: any) => id != null);
              
              if (affectedAgentIds.length > 0) {
                window.dispatchEvent(new CustomEvent("agentUpdated", {
                  detail: { agentIds: affectedAgentIds }
                }));
              }
              
              onAddMessage(
                pendingChanges.next_step || `Successfully updated ${editedRules.length} routing rule(s).`,
                [],
                "assistant"
              );
              onSetPendingChanges(null);
              onSetIsApproving(false);
            }
          },
          error: (error: any) => {
            hasError = true;
            console.error("Error updating routing rule:", error);
            onAddMessage(
              `I encountered an error while updating a routing rule: ${error.message || "Unknown error"}`,
              [],
              "assistant"
            );
            onSetPendingChanges(null);
            onSetIsApproving(false);
          }
        });
      });
    } else if (operation === "delete") {
      // Delete routing rules using RoutingService
      const ruleId = pendingChanges.rule_id;
      const agentId = pendingChanges.agent_id;
      
      if (!ruleId || !agentId) {
        onAddMessage("Error: Missing rule ID or agent ID for routing rule deletion.", [], "assistant");
        onSetPendingChanges(null);
        onSetIsApproving(false);
        return;
      }
      
      RoutingService.deleteRule(agentId, ruleId, apiKey).subscribe({
        next: () => {
          onUpdateMessageSuggestions(message.id, "approved", [{ rule_id: ruleId }]);
          
          // Dispatch agentUpdated event to refresh all agent-related components
          window.dispatchEvent(new CustomEvent("agentUpdated", {
            detail: { agentIds: [agentId] }
          }));
          
          onAddMessage(
            pendingChanges.next_step || "Successfully deleted routing rule.",
            [],
            "assistant"
          );
          onSetPendingChanges(null);
          onSetIsApproving(false);
        },
        error: (error: any) => {
          console.error("Error deleting routing rule:", error);
          onAddMessage(
            `I encountered an error while deleting the routing rule: ${error.message || "Unknown error"}`,
            [],
            "assistant"
          );
          onSetPendingChanges(null);
          onSetIsApproving(false);
        }
      });
    } else if (operation === "list") {
      // List routing rules using RoutingService
      const agentId = pendingChanges.agent_id;
      
      if (!agentId) {
        onAddMessage("Error: No agent ID found for listing routing rules.", [], "assistant");
        onSetPendingChanges(null);
        onSetIsApproving(false);
        return;
      }
      
      RoutingService.getRoutingConfig(agentId, apiKey).subscribe({
        next: (response: any) => {
          const rules = response.rules || [];
          
          if (rules.length === 0) {
            onAddMessage(
              "This agent has no routing rules configured. You can create rules to define how messages should be routed.",
              [],
              "assistant"
            );
          } else {
            // Format the rules for display
            const rulesList = rules.map((rule: any, index: number) => {
              const conditions = rule.conditions || [];
              const conditionsText = conditions.length > 0 
                ? conditions.map((cond: any) => `${cond.variable_path} ${cond.operator} ${cond.value_text || cond.value_number || cond.value_bool || "null"}`).join(" AND ")
                : "No conditions";
              
              const routeText = rule.route?.type === "agent" 
                ? `Agent ${rule.route.agent_id}`
                : rule.route?.type === "action"
                ? `Action ${rule.route.action_id}`
                : rule.route?.type === "llm"
                ? "LLM"
                : "None";
              
              return `${index + 1}. **${rule.label || "Unnamed Rule"}** (${rule.enabled ? "Enabled" : "Disabled"})\n   - Conditions: ${conditionsText}\n   - Route to: ${routeText}`;
            }).join("\n\n");
            
            onAddMessage(
              `Here are the routing rules for this agent:\n\n${rulesList}\n\nTotal: ${rules.length} rule(s)`,
              [],
              "assistant"
            );
          }
          
          onSetPendingChanges(null);
          onSetIsApproving(false);
        },
        error: (error: any) => {
          console.error("Error listing routing rules:", error);
          onAddMessage(
            `I encountered an error while listing routing rules: ${error.message || "Unknown error"}`,
            [],
            "assistant"
          );
          onSetPendingChanges(null);
          onSetIsApproving(false);
        }
      });
    } else {
      onAddMessage(`Unsupported routing operation: ${operation}`, [], "assistant");
      onSetPendingChanges(null);
      onSetIsApproving(false);
    }
  };

  const handleRevertChanges = async (messageId: string, appliedChanges: any[]) => {
    // Check if this is a MANAGE_ROUTING operation
    const isRoutingOperation = appliedChanges.some(change => change.target_type === "routing_rule");
    
    if (isRoutingOperation) {
      // For routing rules, revert by swapping original and proposed
      onSetIsApproving(true);
      
      let remainingReverts = appliedChanges.length;
      let hasError = false;
      
      appliedChanges.forEach((change: any) => {
        const ruleId = change.rule_id;
        const agentId = change.agent_id;
        
        if (!ruleId || !agentId) {
          hasError = true;
          onAddMessage(`Error: Missing rule ID (${ruleId}) or agent ID (${agentId}) for routing rule revert.`, [], "assistant");
          onSetIsApproving(false);
          return;
        }
        
        // Build the complete rule from original (reverting to original state)
        const completeRule = prepareRoutingRuleForSubmission(change.original);
        
        RoutingService.updateRule(agentId, ruleId, completeRule, apiKey).subscribe({
          next: () => {
            remainingReverts--;
            
            if (remainingReverts === 0 && !hasError) {
              onSetRevertedMessageIds(prev => new Set(prev).add(messageId));
              onAddMessage("I've reverted those routing rule changes.", [], "assistant");
              onLoadAgents(true);
              
              // Dispatch agentUpdated event to refresh agent configuration
              const affectedAgentIds = appliedChanges.map((change: any) => change.agent_id).filter(Boolean);
              if (affectedAgentIds.length > 0) {
                window.dispatchEvent(new CustomEvent("agentUpdated", {
                  detail: { agentIds: affectedAgentIds }
                }));
              }
              
              onSetIsApproving(false);
            }
          },
          error: (error: any) => {
            hasError = true;
            console.error("Error reverting routing changes:", error);
            onAddMessage(`I'm sorry, I couldn't revert the routing changes: ${error.message || "Unknown error"}`, [], "assistant");
            onSetIsApproving(false);
          }
        });
      });
    } else {
      // For other changes, validate current draft state before reverting
      // This ensures revert works correctly even if draft was overwritten by drafting from an older version
      onSetIsApproving(true);
      
      try {
        // Get unique agent IDs from changes
        const agentIds = Array.from(new Set(appliedChanges.map((change: any) => change.agent_id).filter(Boolean)));
        
        // Fetch current draft state for all affected agents
        const currentAgents = await Promise.all(
          agentIds.map((agentId: number) => 
            firstValueFrom(AgentService.getAgent(agentId, () => {}, apiKey))
          )
        );
        
        // Build a map of agent_id -> current agent (full agent object, not just config)
        const currentAgentsMap = new Map<number, any>();
        currentAgents.forEach((agent: any) => {
          if (agent && agent.id) {
            currentAgentsMap.set(agent.id, agent);
          }
        });
        
        // Validate each change: check if current state matches expected state
        // If draft was overwritten, current state might not match `proposed` value
        let hasMismatch = false;
        for (const change of appliedChanges) {
          const agentId = change.agent_id;
          const field = change.field;
          const currentAgent = currentAgentsMap.get(agentId);
          
          if (!currentAgent) {
            continue; // Skip validation if agent not found
          }
          
          // Navigate to the field value in current agent
          let currentValue: any;
          
          // Handle top-level fields (agent_name, description)
          if (field === "agent_name" || field === "description") {
            currentValue = currentAgent[field];
          } else if (field.startsWith("agent_config.")) {
            // Handle nested agent_config fields
            const configField = field.substring("agent_config.".length);
            const currentConfig = currentAgent.agent_config || {};
            
            // Navigate through nested path
            currentValue = currentConfig;
            const fieldParts = configField.split(".");
            for (const part of fieldParts) {
              if (currentValue && typeof currentValue === "object" && part in currentValue) {
                currentValue = currentValue[part];
              } else {
                currentValue = undefined;
                break;
              }
            }
          } else {
            // Unknown field format, skip validation
            continue;
          }
          
          // Compare current value with expected `proposed` value
          // Use JSON.stringify for deep comparison
          const currentValueStr = JSON.stringify(currentValue);
          const proposedValueStr = JSON.stringify(change.proposed);
          
          if (currentValueStr !== proposedValueStr) {
            hasMismatch = true;
            console.warn(
              `[Revert Validation] Field ${field} on agent ${agentId} has changed. ` +
              `Expected: ${proposedValueStr}, Current: ${currentValueStr}. ` +
              "Draft may have been overwritten, but revert will still proceed."
            );
          }
        }
        
        // Smart revert: if draft was overwritten, revert to current draft state instead of original
        const revertChanges = appliedChanges.map((change: any) => {
          const agentId = change.agent_id;
          const field = change.field;
          const currentAgent = currentAgentsMap.get(agentId);
          
          if (!currentAgent) {
            // Fallback to normal revert if agent not found
            return {
              ...change,
              original: change.proposed,
              proposed: change.original,
            };
          }
          
          // Get current value from draft
          let currentValue: any;
          
          // Handle top-level fields (agent_name, description)
          if (field === "agent_name" || field === "description") {
            currentValue = currentAgent[field];
          } else if (field.startsWith("agent_config.")) {
            // Handle nested agent_config fields
            const configField = field.substring("agent_config.".length);
            const currentConfig = currentAgent.agent_config || {};
            
            // Navigate through nested path
            currentValue = currentConfig;
            const fieldParts = configField.split(".");
            for (const part of fieldParts) {
              if (currentValue && typeof currentValue === "object" && part in currentValue) {
                currentValue = currentValue[part];
              } else {
                currentValue = undefined;
                break;
              }
            }
          } else {
            // Unknown field format, fallback to normal revert
            return {
              ...change,
              original: change.proposed,
              proposed: change.original,
            };
          }
          
          // Compare current value with expected `proposed` value
          const currentValueStr = JSON.stringify(currentValue);
          const proposedValueStr = JSON.stringify(change.proposed);
          
          if (currentValueStr !== proposedValueStr) {
            // Draft was overwritten - smart revert: restore to current draft state
            return {
              ...change,
              original: change.proposed,
              proposed: currentValue, // Revert to current draft state
            };
          } else {
            // Normal revert: swap original and proposed
            return {
              ...change,
              original: change.proposed,
              proposed: change.original,
            };
          }
        });

        AgentService.approveAssistantChanges(
          revertChanges,
          onSetIsApproving,
          apiKey
        ).subscribe({
          next: () => {
            onSetRevertedMessageIds(prev => new Set(prev).add(messageId));
            onAddMessage("I've reverted those changes.", [], "assistant");
            onLoadAgents(true);
            
            // Dispatch agentUpdated event to refresh agent configuration
            const affectedAgentIds = appliedChanges.map((change: any) => change.agent_id).filter(Boolean);
            if (affectedAgentIds.length > 0) {
              window.dispatchEvent(new CustomEvent("agentUpdated", {
                detail: { agentIds: affectedAgentIds }
              }));
            }
            
            onSetIsApproving(false);
          },
          error: (error) => {
            console.error("Error reverting changes:", error);
            onAddMessage(`I'm sorry, I couldn't revert the changes: ${error.message || "Unknown error"}`, [], "assistant");
            onSetIsApproving(false);
          }
        });
      } catch (error: any) {
        console.error("Error validating draft state before revert:", error);
        // If validation fails, still proceed with revert (fail-safe)
    const revertChanges = appliedChanges.map((change: any) => ({
      ...change,
      original: change.proposed,
      proposed: change.original,
    }));

    AgentService.approveAssistantChanges(
      revertChanges,
      onSetIsApproving,
      apiKey
    ).subscribe({
      next: () => {
        onSetRevertedMessageIds(prev => new Set(prev).add(messageId));
        onAddMessage("I've reverted those changes.", [], "assistant");
        onLoadAgents(true);
        
        const affectedAgentIds = appliedChanges.map((change: any) => change.agent_id).filter(Boolean);
        if (affectedAgentIds.length > 0) {
          window.dispatchEvent(new CustomEvent("agentUpdated", {
            detail: { agentIds: affectedAgentIds }
          }));
        }
        
        onSetIsApproving(false);
      },
      error: (error) => {
        console.error("Error reverting changes:", error);
        onAddMessage(`I'm sorry, I couldn't revert the changes: ${error.message || "Unknown error"}`, [], "assistant");
        onSetIsApproving(false);
      }
    });
      }
    }
  };

  // Render pending suggestions
  if (message.role === "assistant" && message.suggestions && message.suggestionsStatus === "pending") {
    return (
      <div className="mt-4 border rounded-lg bg-card p-4">
        <div className="space-y-4">
          {/* Title based on action type */}
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">
              {message.suggestions.action === "CREATE_AGENT" ? "Create New Agent" :
               message.suggestions.action === "CREATE_ACTION" ? "Create New Capability" :
               message.suggestions.action === "ADD_SUBAGENT" ? "Link Subagent" :
               message.suggestions.action === "MANAGE_ROUTING" ? 
                 (message.suggestions.operation === "create" ? "Create Routing Rule" :
                 message.suggestions.operation === "edit" ? 
                   (message.suggestions.proposed_changes ? `Edit ${message.suggestions.proposed_changes.length} Routing Rules` : "Edit Routing Rule") :
                  message.suggestions.operation === "delete" ? "Delete Routing Rule" :
                  message.suggestions.operation === "list" ? "Routing Rules" : "Manage Routing") :
               "Review Proposed Changes"}
            </h3>
            {(message.suggestions.action === "EDIT" || message.suggestions.action === "EDIT_AGENT" || message.suggestions.action === "EDIT_ACTION") && 
             message.suggestions.proposed_changes && (
              <span className="text-xs text-muted-foreground">
                {selectedChanges.size} of {message.suggestions.proposed_changes.length} selected
              </span>
            )}
          </div>

          {/* Content based on action type */}
          {/* EDIT actions content */}
          {(message.suggestions.action === "EDIT" || message.suggestions.action === "EDIT_AGENT" || message.suggestions.action === "EDIT_ACTION") && 
           message.suggestions.proposed_changes && (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {message.suggestions.proposed_changes.map((change: any, index: number) => (
                <div
                  key={index}
                  className={cn(
                    "border rounded-lg p-3 transition-all",
                    selectedChanges.has(index) 
                      ? "border-maitai-green bg-maitai-green/5" 
                      : "border-border hover:border-maitai-green/50"
                  )}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={selectedChanges.has(index)}
                      onCheckedChange={() => toggleChangeSelection(index)}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium">
                          {change.target_type === "agent" ? "Agent" : "Action"}:
                        </span>
                        <span className="text-sm">
                          {change.agent_name}
                          {change.action_name && ` → ${change.action_name}`}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mb-1">
                        <span className="font-medium">{change.readable_field ? "" : "Field:"}</span> {change.readable_field || change.field}
                      </div>
                      {change.change_reason && (
                        <div className="text-xs bg-muted/50 rounded p-2 mb-2">
                          <span className="font-medium">Reason:</span> {change.change_reason}
                        </div>
                      )}
                      
                      {/* Diff preview */}
                      {(() => {
                        const original = stripRedlineTags(change.original || "");
                        const proposed = stripRedlineTags(change.proposed || "");
                        const { changes: diffChanges, hasMore } = generateDiffPreview(original, proposed);
                        
                        return (
                          <div className="space-y-1">
                            <div className="bg-muted/30 rounded-md p-2 font-mono text-xs space-y-0.5">
                              {diffChanges.map((change, idx) => (
                                <div
                                  key={idx}
                                  className={cn(
                                    "flex items-center gap-2",
                                    change.type === "removed" && "text-red-600 dark:text-red-400",
                                    change.type === "added" && "text-green-600 dark:text-green-400"
                                  )}
                                >
                                  <span className="select-none">
                                    {change.type === "removed" ? "-" : change.type === "added" ? "+" : " "}
                                  </span>
                                  <span className="truncate">{change.line || " "}</span>
                                </div>
                              ))}
                            </div>
                            
                            {/* View full diff button */}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                onSetDiffViewData({
                                  title: `${change.agent_name}${change.action_name ? ` → ${change.action_name}` : ""} (${change.readable_field || change.field})`,
                                  original,
                                  proposed
                                });
                                onSetDiffViewOpen(true);
                              }}
                              className="w-full h-7 text-xs text-muted-foreground hover:text-foreground"
                            >
                              {hasMore ? "Show all changes" : "View in diff mode"}
                              <Eye className="h-3 w-3 ml-1" />
                            </Button>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CREATE_AGENT content */}
          {message.suggestions.action === "CREATE_AGENT" && message.suggestions.agent_data && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Agent Name</Label>
                <p className="text-sm font-medium">{message.suggestions.agent_data.agent_name}</p>
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <p className="text-sm text-muted-foreground">{message.suggestions.agent_data.description}</p>
              </div>
              {message.suggestions.agent_data.base_url && (
                <div>
                  <Label className="text-xs">Base URL</Label>
                  <p className="text-sm font-mono bg-muted px-2 py-1 rounded">{message.suggestions.agent_data.base_url}</p>
                </div>
              )}
              <div>
                <Label className="text-xs">Application</Label>
                {(() => {
                  const presetAppId = message.suggestions.agent_data.application_id;
                  const parentAppId = message.suggestions.agent_data.parent_application_id;
                  const isSubagent = !!message.suggestions.agent_data.parent_agent_id;
                  
                  // If this is a subagent with parent_application_id, show it as locked
                  if (isSubagent && parentAppId) {
                    const parentApp = availableApplications.find(app => app.id === parentAppId);
                    return (
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">{parentApp?.application_name || `Application ID: ${parentAppId}`}</p>
                        <span className="text-xs text-muted-foreground">(inherited from parent)</span>
                      </div>
                    );
                  }
                  
                  // If preset app ID exists, show it as fixed
                  if (presetAppId) {
                    const selectedApp = availableApplications.find(app => app.id === presetAppId);
                    return (
                      <p className="text-sm font-medium">{selectedApp?.application_name || `Application ID: ${presetAppId}`}</p>
                    );
                  }
                  
                  // Otherwise show the dropdown
                  return (
                    <Select 
                      value={selectedApplicationId?.toString() || ""} 
                      onValueChange={(val) => {
                        if (val === "new_application") {
                          setIsNewApplicationModalOpen(true);
                        } else {
                          setSelectedApplicationId(Number(val));
                        }
                      }}
                    >
                      <SelectTrigger className="text-sm h-8">
                        <SelectValue placeholder="Select an application" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableApplications.map((app) => (
                          <SelectItem key={app.id} value={app.id.toString()}>
                            {app.application_name}
                          </SelectItem>
                        ))}
                        <SelectItem value="new_application" className="border-t border-border mt-1 pt-2">
                          <div className="flex items-center gap-2 text-maitai-green font-medium">
                            <Plus className="w-4 h-4" />
                            Create New Application
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>
            </div>
          )}

          {/* CREATE_ACTION content */}
          {message.suggestions.action === "CREATE_ACTION" && message.suggestions.action_data && (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {message.suggestions.action_data.map((action: any, index: number) => (
                <div key={index} className="border rounded-lg p-3">
                  <div className="flex items-start gap-3">
                    {message.suggestions.action_data.length > 1 && (
                      <Checkbox
                        checked={selectedChanges.has(index)}
                        onCheckedChange={() => toggleChangeSelection(index)}
                        className="mt-1"
                      />
                    )}
                    <div className="flex-1 space-y-2">
                      <h4 className="text-sm font-medium">{action.action_name}</h4>
                      <p className="text-xs text-muted-foreground">{action.description}</p>
                      
                      {/* Show which agent this capability is being added to */}
                      {action.agent_id && (
                        <div className="text-xs">
                          <span className="text-muted-foreground">Adding to:</span>{" "}
                          <span className="font-medium">
                            {availableAgents.find((a: any) => a.id === action.agent_id && !a.isAction)?.agent_name || `Agent ID ${action.agent_id}`}
                          </span>
                        </div>
                      )}
                      
                      {/* Show action type and invocation mode */}
                      <div className="flex gap-4 text-xs">
                        <div>
                          <span className="text-muted-foreground">Type:</span>{" "}
                          <span className="font-mono bg-muted px-1.5 py-0.5 rounded">{action.action_type}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Mode:</span>{" "}
                          <span className="font-mono bg-muted px-1.5 py-0.5 rounded">{action.invocation_mode || "foreground"}</span>
                        </div>
                      </div>
                      
                      {/* When to Use */}
                      {action.meta?.when_to_use && (
                        <div className="space-y-1 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded p-2">
                          <Label className="text-xs text-green-900 dark:text-green-100">When to Use</Label>
                          <p className="text-xs text-green-800 dark:text-green-200">
                            {action.meta.when_to_use}
                          </p>
                        </div>
                      )}
                      
                      {/* When NOT to Use */}
                      {action.meta?.when_not_to_use && (
                        <div className="space-y-1 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded p-2">
                          <Label className="text-xs text-red-900 dark:text-red-100">When NOT to Use</Label>
                          <p className="text-xs text-red-800 dark:text-red-200">
                            {action.meta.when_not_to_use}
                          </p>
                        </div>
                      )}
                      
                      {/* Configuration */}
                      <div className="space-y-1">
                        <Label className="text-xs">Configuration</Label>
                        <div className="bg-muted rounded p-2">
                          <pre className="text-xs font-mono whitespace-pre-wrap">
                            {JSON.stringify(action.action_config, null, 2)}
                          </pre>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ADD_SUBAGENT content */}
          {message.suggestions.action === "ADD_SUBAGENT" && message.suggestions.subagent_data && (
            <div className="space-y-3">
              <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-3">
                <div>
                  <Label className="text-xs">Parent Agent</Label>
                  <p className="text-sm font-medium">
                    {availableAgents.find((a: any) => a.id === message.suggestions.subagent_data.parent_agent_id)?.agent_name || `Agent ${message.suggestions.subagent_data.parent_agent_id}`}
                  </p>
                </div>
                
                <div className="flex justify-center">
                  <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
                
                <div>
                  <Label className="text-xs">Subagent</Label>
                  <p className="text-sm font-medium">
                    {availableAgents.find((a: any) => a.id === message.suggestions.subagent_data.child_agent_id)?.agent_name || `Agent ${message.suggestions.subagent_data.child_agent_id}`}
                  </p>
                </div>
              </div>
              
              {message.suggestions.explanation && (
                <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded p-3">
                  <p className="text-xs text-blue-800 dark:text-blue-200">{message.suggestions.explanation}</p>
                </div>
              )}
            </div>
          )}
          
          {/* MANAGE_ROUTING content */}
          {message.suggestions.action === "MANAGE_ROUTING" && (
            <div className="space-y-3">
              {/* List operation - show existing rules */}
              {message.suggestions.operation === "list" && message.suggestions.rules && (
                <div className="space-y-2">
                  {message.suggestions.rules.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No routing rules found.</p>
                  ) : (
                    message.suggestions.rules.map((rule: any, index: number) => (
                      <div key={rule.id || index} className="border rounded-lg p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium">{rule.label}</h4>
                          <span className={cn(
                            "text-xs px-2 py-0.5 rounded-full",
                            rule.enabled ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                          )}>
                            {rule.enabled ? "Enabled" : "Disabled"}
                          </span>
                        </div>
                        <div className="text-xs space-y-1">
                          <div>
                            <span className="text-muted-foreground">Route to:</span>{" "}
                            <span className="font-mono bg-muted px-1.5 py-0.5 rounded">
                              {rule.route_type === "agent" && rule.route_agent_id ? 
                                (rule.route_agent_name || `Agent ${rule.route_agent_id}`) :
                               rule.route_type === "action" && rule.route_action_id ? 
                                (rule.route_action_name || `Action ${rule.route_action_id}`) :
                               rule.route_type === "llm" ? "LLM" :
                               "None"}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Conditions:</span> {rule.conditions?.length || 0}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
              
              {/* Create/Edit operation - show rule details */}
              {(message.suggestions.operation === "create" || message.suggestions.operation === "edit") && 
               (message.suggestions.routing_rules || message.suggestions.routing_rule || message.suggestions.proposed_changes) && (
                <div className="space-y-3">
                  {/* Handle both array and single rule formats */}
                  {(() => {
                    // For edits, use proposed_changes format
                    if (message.suggestions.proposed_changes) {
                      return message.suggestions.proposed_changes.map((change: any, idx: number) => (
                        <div key={idx} className="border rounded-lg p-3 space-y-3 max-w-full overflow-hidden">
                          <div className="space-y-2">
                            <div>
                              <Label className="text-xs">
                                {change.original?.label || `Rule #${change.rule_id}`}
                                {change.proposed?.label !== change.original?.label && 
                                  ` → ${change.proposed?.label}`}
                              </Label>
                              {change.change_reason && (
                                <p className="text-xs text-muted-foreground">{change.change_reason}</p>
                              )}
                            </div>
                            
                            {/* Show what's changing */}
                            {change.proposed && (
                              <div className="space-y-3">
                                {/* Show conditions with tree structure */}
                                {change.proposed.conditions && change.proposed.conditions.length > 0 && (
                                  <div>
                                    <Label className="text-xs">Conditions</Label>
                                    <div className="mt-1">
                                      <ConditionTreeView conditions={change.proposed.conditions} />
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ));
                    }
                    
                    // Original logic for create operations
                    const rules = message.suggestions.routing_rules || [message.suggestions.routing_rule];
                    return rules.map((rule: any, ruleIdx: number) => (
                      <div key={ruleIdx} className="border rounded-lg p-3 space-y-3 max-w-full overflow-hidden">
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs">Rule Label</Label>
                            <p className="text-sm font-medium">{rule.label}</p>
                    </div>
                    
                    <div>
                      <Label className="text-xs">What happens when this rule matches</Label>
                      <p className="text-sm">
                        {rule.route_type === "action" && (
                          <>
                            <span className="font-medium">Execute capability: </span>
                        <span className="font-mono bg-muted px-1.5 py-0.5 rounded">
                              {rule.route_action_name || 
                               availableAgents.find((a: any) => a.id === rule.route_action_id && a.isAction)?.agent_name || 
                               `Action ${rule.route_action_id}`}
                        </span>
                          </>
                        )}
                        {rule.route_type === "agent" && (
                          <>
                            <span className="font-medium">Route to agent: </span>
                            <span className="font-mono bg-muted px-1.5 py-0.5 rounded">
                              {rule.route_agent_name || 
                               availableAgents.find((a: any) => a.id === rule.route_agent_id && !a.isAction)?.agent_name || 
                               `Agent ${rule.route_agent_id}`}
                          </span>
                          </>
                        )}
                        {rule.route_type === "llm" && (
                          <>
                            <span className="font-medium">Continue with LLM processing</span>
                            <span className="text-xs text-muted-foreground ml-2">(bypass rules, use AI)</span>
                          </>
                        )}
                        {rule.route_type === "none" && (
                          <>
                            <span className="font-medium">Stop processing</span>
                            <span className="text-xs text-muted-foreground ml-2">(end conversation)</span>
                          </>
                        )}
                      </p>
                    </div>
                    
                    {/* Conditions */}
                    {rule.conditions && 
                     rule.conditions.length > 0 && (
                      <div>
                        <Label className="text-xs">When to trigger this rule</Label>
                        <div className="mt-1">
                          <ConditionTreeView conditions={rule.conditions} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ));
            })()}
                </div>
              )}
              
              {/* Delete operation - show rule to delete */}
              {message.suggestions.operation === "delete" && (
                <div className="border border-destructive/50 rounded-lg p-3 bg-destructive/5">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-destructive mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Delete Routing Rule</p>
                      <p className="text-xs text-muted-foreground">
                        Rule ID: {message.suggestions.rule_id}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        This action cannot be undone. Remaining rules will be automatically reordered.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Action buttons */}
          <div className="flex items-center justify-between gap-2">
            <Button
              onClick={handleRejectChanges}
              size="sm"
              variant="outline"
              className="border-red-200 hover:border-red-300 hover:bg-red-50 dark:border-red-800 dark:hover:border-red-700 dark:hover:bg-red-950/50 text-red-600 dark:text-red-400"
              disabled={isApproving}
            >
              <X className="h-4 w-4 mr-1" />
              Reject
            </Button>
            
            <div className="flex items-center gap-2">
              {/* Conditional approve buttons based on selection for EDIT and CREATE_ACTION */}
              {((message.suggestions.action === "EDIT" || message.suggestions.action === "EDIT_AGENT" || message.suggestions.action === "EDIT_ACTION") && 
               message.suggestions.proposed_changes && 
               selectedChanges.size > 0 && 
               selectedChanges.size < message.suggestions.proposed_changes.length) ||
               (message.suggestions.action === "CREATE_ACTION" && 
                message.suggestions.action_data && 
                message.suggestions.action_data.length > 1 &&
                selectedChanges.size > 0 && 
                selectedChanges.size < message.suggestions.action_data.length) ? (
                <>
                  <Button
                    key="approve-all-white"
                    onClick={() => {
                      onSetPendingChanges(message.suggestions);
                      
                      if (message.suggestions.action === "CREATE_ACTION") {
                        const allIndices = new Set<number>(message.suggestions.action_data.map((_: any, idx: number) => idx));
                        onSetSelectedChanges(allIndices);
                        handleCreateAction(allIndices);
                      } else {
                        const allIndices = new Set<number>(message.suggestions.proposed_changes.map((_: any, idx: number) => idx));
                        onSetSelectedChanges(allIndices);
                        handleApproveChanges(allIndices);
                      }
                    }}
                    size="sm"
                    variant="outline"
                    className="bg-white hover:bg-gray-50 dark:bg-gray-950 dark:hover:bg-gray-900 border-border"
                    disabled={isApproving}
                  >
                    <Check className="h-4 w-4 mr-1" />
                    {message.suggestions.action === "CREATE_ACTION" ? "Create All" : "Approve All"}
                  </Button>
                  <Button
                    key="approve-selected-green"
                    onClick={() => {
                      onSetPendingChanges(message.suggestions);
                      if (message.suggestions.action === "CREATE_ACTION") {
                        handleCreateAction(selectedChanges);
                      } else {
                        handleApproveChanges(selectedChanges);
                      }
                    }}
                    size="sm"
                    className="bg-maitai-green hover:bg-maitai-green/90 text-white border-0"
                    disabled={isApproving}
                  >
                    <Check className="h-4 w-4 mr-1" />
                    {message.suggestions.action === "CREATE_ACTION" ? "Create Selected" : "Approve Selected"}
                  </Button>
                </>
              ) : (
                <Button
                  key="approve-single-green"
                  onClick={() => {
                    onSetPendingChanges(message.suggestions);
                    if (message.suggestions.action === "CREATE_AGENT") {
                      handleCreateAgent();
                    } else if (message.suggestions.action === "CREATE_ACTION") {
                      handleCreateAction();
                    } else if (message.suggestions.action === "ADD_SUBAGENT") {
                      handleAddSubagent();
                    } else if (message.suggestions.action === "MANAGE_ROUTING") {
                      handleManageRouting();
                    } else {
                      handleApproveChanges();
                    }
                  }}
                  size="sm"
                  className="bg-maitai-green hover:bg-maitai-green/90 text-white border-0"
                  disabled={isApproving}
                >
                  <Check className="h-4 w-4 mr-1" />
                  {message.suggestions.action === "CREATE_AGENT" ? "Create Agent" :
                   message.suggestions.action === "CREATE_ACTION" ? "Create" :
                   message.suggestions.action === "ADD_SUBAGENT" ? "Link" :
                   message.suggestions.action === "MANAGE_ROUTING" ? 
                     (message.suggestions.operation === "create" ? "Create Rule" :
                      message.suggestions.operation === "edit" ? "Update Rule" :
                      message.suggestions.operation === "delete" ? "Delete Rule" :
                      message.suggestions.operation === "list" ? "Show Rules" : "Execute") :
                   "Approve All"}
                </Button>
              )}
            </div>
          </div>
        </div>
        
        {/* New Application Modal */}
        {isNewApplicationModalOpen && (
          <NewApplicationModal
            isOpen={isNewApplicationModalOpen}
            onClose={() => setIsNewApplicationModalOpen(false)}
            onApplicationCreated={(app: Application) => {
              setSelectedApplicationId(app.id);
              ApplicationService.getApplications(() => {}, apiKey).subscribe();
              setIsNewApplicationModalOpen(false);
            }}
          />
        )}
      </div>
    );
  }

  // Render processed suggestions (approved/rejected/ignored)
  if (message.role === "assistant" && message.suggestions && message.suggestionsStatus !== "pending") {
    return (
      <>
        <div 
          className="flex items-center gap-2 mt-2 text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
          onClick={() => onToggleMessageExpanded(message.id)}
        >
          <div className="h-px flex-1 bg-border" />
          <span className="px-2 flex items-center gap-1">
            {message.suggestionsExpanded ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
            {revertedMessageIds.has(message.id) ? "Changes Reverted" :
             message.suggestionsStatus === "approved" ? "Changes Approved" :
             message.suggestionsStatus === "rejected" ? "Changes Rejected" :
             message.suggestionsStatus === "ignored" ? "Changes Ignored" :
             "Some Changes Approved"}
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>
        
        {/* Expanded view of processed suggestions */}
        {message.suggestionsExpanded && (
          <div className="mt-3 border rounded-lg bg-muted/30 p-4 opacity-75">
            <div className="space-y-4">
              {/* Show the same content as above but read-only */}
              {/* EDIT actions content */}
              {(message.suggestions.action === "EDIT" || message.suggestions.action === "EDIT_AGENT" || message.suggestions.action === "EDIT_ACTION") && 
               message.suggestions.proposed_changes && (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {message.suggestions.proposed_changes.map((change: any, index: number) => (
                    <div
                      key={index}
                      className="border rounded-lg p-3 bg-background/50"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium">
                            {change.target_type === "agent" ? "Agent" : "Action"}:
                          </span>
                          <span className="text-sm">
                            {change.agent_name}
                            {change.action_name && ` → ${change.action_name}`}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground mb-1">
                          <span className="font-medium">{change.readable_field ? "" : "Field:"}</span> {change.readable_field || change.field}
                        </div>
                        {change.change_reason && (
                          <div className="text-xs bg-muted/50 rounded p-2 mb-2">
                            <span className="font-medium">Reason:</span> {change.change_reason}
                          </div>
                        )}
                        
                        {/* Diff preview */}
                        {(() => {
                          const original = stripRedlineTags(change.original || "");
                          const proposed = stripRedlineTags(change.proposed || "");
                          const { changes: diffChanges, hasMore } = generateDiffPreview(original, proposed);
                          
                          return (
                            <div className="space-y-1">
                              <div className="bg-muted/30 rounded-md p-2 font-mono text-xs space-y-0.5">
                                {diffChanges.map((change, idx) => (
                                  <div
                                    key={idx}
                                    className={cn(
                                      "flex items-center gap-2",
                                      change.type === "removed" && "text-red-600 dark:text-red-400",
                                      change.type === "added" && "text-green-600 dark:text-green-400"
                                    )}
                                  >
                                    <span className="select-none">
                                      {change.type === "removed" ? "-" : change.type === "added" ? "+" : " "}
                                    </span>
                                    <span className="truncate">{change.line || " "}</span>
                                  </div>
                                ))}
                              </div>
                              
                              {/* View full diff button */}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  onSetDiffViewData({
                                    title: `${change.agent_name}${change.action_name ? ` → ${change.action_name}` : ""} (${change.readable_field || change.field})`,
                                    original,
                                    proposed
                                  });
                                  onSetDiffViewOpen(true);
                                }}
                                className="w-full h-7 text-xs text-muted-foreground hover:text-foreground opacity-100"
                              >
                                {hasMore ? "Show all changes" : "View in diff mode"}
                                <Eye className="h-3 w-3 ml-1" />
                              </Button>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* CREATE_AGENT content */}
              {message.suggestions.action === "CREATE_AGENT" && message.suggestions.agent_data && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Agent Name</Label>
                    <p className="text-sm font-medium">{message.suggestions.agent_data.agent_name}</p>
                  </div>
                  <div>
                    <Label className="text-xs">Description</Label>
                    <p className="text-sm text-muted-foreground">{message.suggestions.agent_data.description}</p>
                  </div>
                  {message.suggestions.agent_data.base_url && (
                    <div>
                      <Label className="text-xs">Base URL</Label>
                      <p className="text-sm font-mono bg-muted px-2 py-1 rounded">{message.suggestions.agent_data.base_url}</p>
                    </div>
                  )}
                </div>
              )}

              {/* MANAGE_ROUTING content */}
              {message.suggestions.action === "MANAGE_ROUTING" && (
                <div className="space-y-3">
                  {message.suggestions.operation === "list" && message.suggestions.existing_rules && (
                    <div className="space-y-2">
                      <Label className="text-xs">Existing Rules</Label>
                      {message.suggestions.existing_rules.map((rule: any, idx: number) => (
                        <div key={idx} className="border rounded p-2 text-sm">
                          <div className="font-medium">{rule.label}</div>
                          <div className="text-xs text-muted-foreground">
                            Routes to: {rule.route_type === "action" ? 
                              (rule.route_action_name || `Action #${rule.route_action_id}`) :
                              rule.route_type === "agent" ?
                              (rule.route_agent_name || `Agent #${rule.route_agent_id}`) :
                              rule.route_type}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {(message.suggestions.operation === "create" || message.suggestions.operation === "edit") && (
                    <div className="space-y-3">
                      {(() => {
                        // If changes were applied, show the applied changes instead
                        if (message.appliedChanges && message.appliedChanges.length > 0 && message.suggestionsStatus === "approved") {
                          return message.appliedChanges.map((change: any, idx: number) => {
                            // Handle both old format (direct rule) and new format (original/proposed)
                            const rule = change.proposed || change;
                            
                            return (
                              <div key={idx} className="border rounded-lg p-3 space-y-3">
                                <div className="space-y-2">
                                  <div>
                                    <Label className="text-xs">Rule: {rule.label || `Rule #${rule.id}`}</Label>
                                    {change.original && change.original.label !== rule.label && (
                                      <p className="text-xs text-muted-foreground">Previously: {change.original.label}</p>
                                    )}
                                  </div>
                                  
                                  {rule.route_type && (
                                    <div>
                                      <Label className="text-xs">Route Type</Label>
                                      <p className="text-sm">
                                        {rule.route_type === "action" && (
                                          <>Execute: {rule.route_action_name || `Action #${rule.route_action_id}`}</>
                                        )}
                                        {rule.route_type === "agent" && (
                                          <>Route to: {rule.route_agent_name || `Agent #${rule.route_agent_id}`}
                                            {rule.route_action_id && (
                                              <> → {rule.route_action_name || `Action #${rule.route_action_id}`}</>
                                            )}
                                          </>
                                        )}
                                        {rule.route_type === "llm" && "Continue with LLM"}
                                        {rule.route_type === "none" && "Stop processing"}
                                      </p>
                                    </div>
                                  )}
                                  
                                  {rule.conditions && rule.conditions.length > 0 && (
                                    <div>
                                      <Label className="text-xs">Conditions</Label>
                                      <div className="mt-1">
                                        <ConditionTreeView conditions={rule.conditions} />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          });
                        }
                        
                        // Handle proposed_changes for edits
                        if (message.suggestions.proposed_changes) {
                          return message.suggestions.proposed_changes.map((change: any, idx: number) => (
                            <div key={idx} className="border rounded-lg p-3 space-y-3">
                              <div className="space-y-2">
                                <div>
                                  <Label className="text-xs">
                                    {change.original?.label || `Rule #${change.rule_id}`}
                                    {change.proposed?.label !== change.original?.label && 
                                      ` → ${change.proposed?.label}`}
                                  </Label>
                                  {change.change_reason && (
                                    <p className="text-xs text-muted-foreground">{change.change_reason}</p>
                                  )}
                                </div>
                                
                                {/* Show conditions */}
                                {change.proposed?.conditions && change.proposed.conditions.length > 0 && (
                                  <div>
                                    <Label className="text-xs">Conditions</Label>
                                    <div className="mt-1">
                                      <ConditionTreeView conditions={change.proposed.conditions} />
                                    </div>
                                  </div>
                                )}
                                
                                {/* Show enabled status change */}
                                {change.proposed?.enabled !== change.original?.enabled && (
                                  <div>
                                    <Label className="text-xs">Enabled</Label>
                                    <p className="text-sm">
                                      {change.original?.enabled ? "Yes" : "No"} → {change.proposed?.enabled ? "Yes" : "No"}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ));
                        }
                        
                        // Handle single rule or array of rules (only if not showing applied changes)
                        if (message.suggestions.routing_rules || message.suggestions.routing_rule) {
                          const rules = message.suggestions.routing_rules || [message.suggestions.routing_rule];
                          return rules.map((rule: any, ruleIdx: number) => (
                          <div key={ruleIdx} className="border rounded-lg p-3 space-y-3">
                            <div className="space-y-2">
                              <div>
                                <Label className="text-xs">Rule Label</Label>
                                <p className="text-sm font-medium">{rule.label}</p>
                              </div>
                              
                              <div>
                                <Label className="text-xs">Route Type</Label>
                                <p className="text-sm">
                                  {rule.route_type === "action" && (
                                    <>Execute: {rule.route_action_name || `Action #${rule.route_action_id}`}</>
                                  )}
                                  {rule.route_type === "agent" && (
                                    <>Route to: {rule.route_agent_name || `Agent #${rule.route_agent_id}`}
                                    {rule.route_action_id && ` → ${rule.route_action_name || `Action #${rule.route_action_id}`}`}</>
                                  )}
                                  {rule.route_type === "llm" && "Continue with LLM"}
                                  {rule.route_type === "none" && "Stop processing"}
                                </p>
                              </div>
                              
                              {rule.conditions && rule.conditions.length > 0 && (
                                <div>
                                  <Label className="text-xs">Conditions</Label>
                                  <div className="mt-1">
                                    <ConditionTreeView conditions={rule.conditions} />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ));
                        }
                        
                        // Return empty array if no data to show
                        return [];
                      })()}
                    </div>
                  )}
                </div>
              )}

              {/* CREATE_ACTION content */}
              {message.suggestions.action === "CREATE_ACTION" && message.suggestions.action_data && (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {message.suggestions.action_data.map((action: any, index: number) => (
                    <div key={index} className="border rounded-lg p-3 bg-background/50">
                      <div className="flex-1 space-y-2">
                        <h4 className="text-sm font-medium">{action.action_name}</h4>
                        <p className="text-xs text-muted-foreground">{action.description}</p>
                        
                        {/* Show which agent this capability is being added to */}
                        {action.agent_id && (
                          <div className="text-xs">
                            <span className="text-muted-foreground">Added to:</span>{" "}
                            <span className="font-medium">
                              {availableAgents.find((a: any) => a.id === action.agent_id && !a.isAction)?.agent_name || `Agent ID ${action.agent_id}`}
                            </span>
                          </div>
                        )}
                        
                        {/* Show action type and invocation mode */}
                        <div className="flex gap-4 text-xs">
                          <div>
                            <span className="text-muted-foreground">Type:</span>{" "}
                            <span className="font-mono bg-muted px-1.5 py-0.5 rounded">{action.action_type}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Mode:</span>{" "}
                            <span className="font-mono bg-muted px-1.5 py-0.5 rounded">{action.invocation_mode || "foreground"}</span>
                          </div>
                        </div>
                        
                        {/* When to Use */}
                        {action.meta?.when_to_use && (
                          <div className="space-y-1 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded p-2">
                            <Label className="text-xs text-green-900 dark:text-green-100">When to Use</Label>
                            <p className="text-xs text-green-800 dark:text-green-200">
                              {action.meta.when_to_use}
                            </p>
                          </div>
                        )}
                        
                        {/* When NOT to Use */}
                        {action.meta?.when_not_to_use && (
                          <div className="space-y-1 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded p-2">
                            <Label className="text-xs text-red-900 dark:text-red-100">When NOT to Use</Label>
                            <p className="text-xs text-red-800 dark:text-red-200">
                              {action.meta.when_not_to_use}
                            </p>
                          </div>
                        )}
                        
                        {/* Configuration */}
                        <div className="space-y-1">
                          <Label className="text-xs">Configuration</Label>
                          <div className="bg-muted rounded p-2">
                            <pre className="text-xs font-mono whitespace-pre-wrap">
                              {JSON.stringify(action.action_config, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ADD_SUBAGENT content */}
              {message.suggestions.action === "ADD_SUBAGENT" && message.suggestions.subagent_data && (
                <div className="space-y-3">
                  <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-3">
                    <div>
                      <Label className="text-xs">Parent Agent</Label>
                      <p className="text-sm font-medium">
                        {availableAgents.find((a: any) => a.id === message.suggestions.subagent_data.parent_agent_id)?.agent_name || `Agent ${message.suggestions.subagent_data.parent_agent_id}`}
                      </p>
                    </div>
                    
                    <div className="flex justify-center">
                      <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                      </svg>
                    </div>
                    
                    <div>
                      <Label className="text-xs">Subagent</Label>
                      <p className="text-sm font-medium">
                        {availableAgents.find((a: any) => a.id === message.suggestions.subagent_data.child_agent_id)?.agent_name || `Agent ${message.suggestions.subagent_data.child_agent_id}`}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Revert button - only show if changes were applied and not already reverted */}
            {/* Don't show for CREATE operations */}
            {message.appliedChanges && message.appliedChanges.length > 0 && !revertedMessageIds.has(message.id) && 
             !(message.suggestions?.action === "MANAGE_ROUTING" && message.suggestions?.operation === "create") && (
              <div className="mt-4 pt-4 border-t border-border/50">
                <Button
                  onClick={() => handleRevertChanges(message.id, message.appliedChanges!)}
                  size="sm"
                  variant="outline"
                  className="gap-2 text-xs text-orange-600 hover:text-orange-700 border-orange-200 hover:border-orange-300"
                  disabled={isApproving}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Revert Changes
                </Button>
              </div>
            )}
            
            {/* Show reverted message if changes were reverted */}
            {message.appliedChanges && message.appliedChanges.length > 0 && revertedMessageIds.has(message.id) && (
              <div className="mt-4 pt-4 border-t border-border/50">
                <div className="text-xs text-muted-foreground">
                  These changes have been reverted
                </div>
              </div>
            )}
          </div>
        )}
      </>
    );
  }

  return null;
};
